import { useEffect, useRef, useState } from 'react';
import type { ReactNode, RefObject } from 'react';
import type { IconDefinition } from '../../shared/iconDefinition';
import { TwemojiGlyphImage } from '../twemojiGlyphImage';

type FloatingPulse = { key: number; iconId: string; side: 'L' | 'R'; top: number };

// The watch screen. Ported from prototype-tap-test.html, which existed to answer
// one question (DESIGN-DECISIONS §10): can a person hit the icon they meant,
// while watching, without losing the video?
//
// The layout is the answer and is carried over verbatim — video centred, icons in
// the letterbox margins either side, five left and four right. Both columns mix
// positive and negative on purpose, so neither side becomes "the bad side": in
// landscape both thumbs rest at the edges and reachability has to be symmetric.
//
// It renders in two modes against the SAME player instance. 'capture' is the
// reviewer's first watch. 'replay' is the summary-screen payoff, and reuses this
// screen rather than building a second player so the video does not reload
// between them. In replay mode the tap columns are gone: this reviewer is done,
// and there is nothing left to record.
//
// Note what is NOT here in capture mode: any sight of anyone else's reactions.
// During a first watch that would correlate every observation after the first,
// and at N~12 that is the dataset (spec §6.3).
export function ViewerWatchScreen(input: {
  active: boolean;
  mode: 'capture' | 'replay';
  icons: IconDefinition[];
  tapCount: number;
  onRecord: (iconId: string, method: 'tap' | 'key') => void;
  onFinish: () => void;
  onExitReplay: () => void;
  replayOverlay: ReactNode;
  playerMountRef: RefObject<HTMLDivElement | null>;
}) {
  const [pulses, setPulses] = useState<FloatingPulse[]>([]);
  const pulseKeyRef = useRef(0);

  const capturingRef = useRef(false);
  capturingRef.current = input.active && input.mode === 'capture';

  const iconsRef = useRef(input.icons);
  iconsRef.current = input.icons;
  const onRecordRef = useRef(input.onRecord);
  onRecordRef.current = input.onRecord;

  const fire = (iconId: string, method: 'tap' | 'key') => {
    onRecordRef.current(iconId, method);

    // Haptics on Android; a no-op on iOS Safari, which never implemented
    // navigator.vibrate. The visual pulse below is the feedback that actually
    // has to work, on every device.
    if (typeof navigator.vibrate === 'function') navigator.vibrate(18);

    const icon = iconsRef.current.find((candidate) => candidate.id === iconId);
    if (!icon) return;
    pulseKeyRef.current += 1;
    const pulse: FloatingPulse = {
      key: pulseKeyRef.current,
      iconId,
      side: icon.column_side,
      top: 20 + Math.random() * 50
    };
    setPulses((current) => [...current, pulse].slice(-16));
    window.setTimeout(() => {
      setPulses((current) => current.filter((candidate) => candidate.key !== pulse.key));
    }, 900);
  };

  // Keyboard 1-9 on desktop. `method` is recorded per reaction so tap-vs-key lag
  // can be compared later — the per-icon offsets in the icon set are guesses, and
  // a mouse is not a thumb.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!capturingRef.current) return;
      const digit = Number.parseInt(event.key, 10);
      if (!Number.isFinite(digit) || digit < 1 || digit > iconsRef.current.length) return;
      const icon = iconsRef.current[digit - 1];
      if (icon) fire(icon.id, 'key');
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const renderColumn = (side: 'L' | 'R') => (
    <div className="iconColumn">
      {input.icons.map((icon, index) => {
        if (icon.column_side !== side) return null;
        return (
          <button
            key={icon.id}
            type="button"
            className="iconButton"
            aria-label={icon.label}
            onClick={() => fire(icon.id, 'tap')}
          >
            <TwemojiGlyphImage codepoint={icon.glyph_cp} label={icon.label} size={34} />
            <span className="iconButtonKey">{index + 1}</span>
          </button>
        );
      })}
      {pulses
        .filter((pulse) => pulse.side === side)
        .map((pulse) => {
          const icon = input.icons.find((candidate) => candidate.id === pulse.iconId);
          if (!icon) return null;
          return (
            <span className="tapPulse" key={pulse.key} style={{ top: `${pulse.top}%` }}>
              <TwemojiGlyphImage codepoint={icon.glyph_cp} label={icon.label} size={28} />
            </span>
          );
        })}
    </div>
  );

  const capturing = input.mode === 'capture';

  return (
    <section className={input.active ? 'screen screenOn screenWatch' : 'screen screenWatch'}>
      {capturing ? renderColumn('L') : null}

      <div className="videoStage">
        {capturing ? (
          <>
            <div className="tapCounter">{input.tapCount}</div>
            <button className="finishButton" type="button" onClick={input.onFinish}>
              Finish
            </button>
          </>
        ) : (
          <div className="replayBar">
            <span>Everyone's reactions</span>
            <button type="button" onClick={input.onExitReplay}>
              Back to my summary
            </button>
          </div>
        )}

        {/* Owned by the flow component, not by React, so switching screens or
            modes never unmounts the iframe and reloads the video. */}
        <div className="playerMount" ref={input.playerMountRef} />

        {/* In blind capture the overlay is present but disabled, so it renders
            nothing. In live capture it runs alongside the tap columns; in replay
            it runs without them. */}
        {input.replayOverlay}
      </div>

      {capturing ? renderColumn('R') : null}
    </section>
  );
}
